// cast.ts — (B101; Creative 2 C2.6-1, planning 10-22 13:50) Admin → Cast, pure: the cast candidates picker's form, its
// set's state in words, and what can be picked. The customer words, approved by the owner (10-22 14:05): "Pick the hands
// for your series" (…the creature…, …the group…) and "These are made by AI from your description".
export const CAST_KINDS = [
  { kind: "hands", label: "A pair of hands" }, { kind: "creature", label: "A creature" }, { kind: "group", label: "A group" },
] as const;
export const CAST_STYLES = [{ style: "photoreal", label: "Photoreal" }, { style: "stylized", label: "Stylized" }, { style: "cartoon", label: "Cartoon" }] as const;
export type CastKind = (typeof CAST_KINDS)[number]["kind"];
/** (approved 10-22 14:05) the heading, by what's being cast, and the note under it */
export const castTitle = (kind: CastKind) => `Pick the ${kind} for your series`;
export const CAST_AI_NOTE = "These are made by AI from your description";
export const VIEW_LABEL: Record<string, string> = { front: "Front", angle: "Angle", in_use: "In use" };
export const VIEWS = ["front", "angle", "in_use"] as const;
export const CAST_COST_NOTE = "Twelve AI images (four candidates, each from the front, at an angle and in use), within the engine's cap ($1.20 by default). It takes up to 15 minutes. Nothing is used until you pick one.";
export const CAST_HISTORY_KEY = "tf.admin.cast.seen";
export const CAST_POLL_MS = 10_000, CAST_POLL_LIMIT_MS = 21 * 60_000;   // the engine reads a set still making after 20 min as failed

export interface CastForm { entityKind: CastKind; name: string; description: string; groupCount: string; style: string }
export interface CastImage { candidate: number; view: string; url: string; rights: string }
export interface CastSet {
  version: string; id: string; entityKind: CastKind; name: string; description: string; groupCount: number | null; style: string;
  status: "generating" | "ready" | "failed" | "picked"; centsMax: number | null; spentCents: number; stopped: string | null; recorded: boolean | null;
  candidates: { candidate: number; images: CastImage[] }[]; pickable: number[]; picked: number | Record<string, unknown> | null;
  wordsProposed: { title: string | null; note: string };
}

/** Pure: the form → the body the backend takes, or what's wrong (the contract's own limits). */
export function castBody(f: CastForm): { body: { entityKind: CastKind; name: string; description: string; groupCount?: number; style: string } } | { off: string } {
  const name = f.name.trim(), description = f.description.trim();
  if (name.length < 2 || name.length > 60) return { off: "A name of 2–60 characters." };
  if (description.length < 10 || description.length > 400) return { off: "A description of 10–400 characters." };
  if (f.entityKind === "group") {
    const n = Number(f.groupCount);
    if (!Number.isInteger(n) || n < 2 || n > 12) return { off: "How many in the group: 2–12." };
    return { body: { entityKind: f.entityKind, name, description, groupCount: n, style: f.style } };
  }
  return { body: { entityKind: f.entityKind, name, description, style: f.style } };
}
const money = (c: number) => `$${(Math.max(0, c) / 100).toFixed(2)}`;
/** Pure: the set's state in words. */
export function castState(s: Pick<CastSet, "status" | "stopped" | "spentCents" | "centsMax" | "picked">): string {
  const spent = `Spent ${money(s.spentCents)}${s.centsMax == null ? "" : ` of ${money(s.centsMax)}`}.`;   // (audit 10-23) no ceiling yet: none named
  switch (s.status) {
    case "generating": return `Making the candidates (up to 15 minutes)… ${spent}`;
    case "ready": return `Ready: pick one. ${spent}${s.stopped ? ` Fewer were made: ${s.stopped}` : ""}`;
    case "failed": return `These couldn't be made: ${s.stopped ?? "no reason was given"}. ${spent}`;
    case "picked": return `Picked: candidate ${pickedOf(s) ?? "?"}. ${spent}`;
  }
}
export const pickedOf = (s: Pick<CastSet, "picked">): number | null =>
  typeof s.picked === "number" ? s.picked : s.picked && typeof (s.picked as { candidate?: unknown }).candidate === "number" ? (s.picked as { candidate: number }).candidate : null;
/** Pure: a candidate's pick button — offered only on a ready set, for a candidate with all three views. */
export function pickOffer(s: Pick<CastSet, "status" | "pickable">, candidate: number): { show: boolean; enabled: boolean; note: string | null } {
  if (s.status !== "ready") return { show: false, enabled: false, note: null };
  return s.pickable.includes(candidate) ? { show: true, enabled: true, note: null } : { show: true, enabled: false, note: "Not all three views were made: it can't be picked." };
}
/** Pure: whether to read the set again (making, within the limit). */
export const castPollNext = (status: CastSet["status"] | null, startedAt: number, now: number) =>
  status !== "generating" ? "stop" : now - startedAt >= CAST_POLL_LIMIT_MS ? "timeout" : "poll";
/** Each refusal the picker can meet, in words; the engine's own message follows. */
export const CAST_REFUSAL_WORDS: Record<string, string> = {
  VALIDATION_ERROR: "Something in the form isn't right.",
  CAST_DESCRIPTION_REFUSED: "The description names something real (a person, a brand, a place, a link or a handle) or asks for a photo of it.",
  CAST_NOT_PICKABLE: "This set can't be picked now (still being made, or already picked).",
  CAST_CANDIDATE_INCOMPLETE: "That candidate doesn't have all three views.",
  AUDIT_UNAVAILABLE: "The pick couldn't be recorded, so nothing changed. Try again.",
  CAST_NOT_AVAILABLE: "Cast candidates come with Creative 2's next deploy.",
  ENGINE_SHAPE: "The engine's answer couldn't be read.",
};
export function castRefusalText(e: unknown): string {
  const x = e as { code?: string; message?: string } | null;
  const w = x?.code ? CAST_REFUSAL_WORDS[x.code] : undefined, m = x?.message?.trim();
  return w ? (m && m !== w ? `${w} (${m})` : w) : m || "not done; try again";
}
